import { handleDeleteApi, handlePatchUpdate, handlePostApi } from '@/api/crud';
import { customerApiEndpoints } from '@/api/endpoints';
import ConfirmModal from './confirmModal';
import { useFetch } from '@/hooks';
import ChangePasswordModal from '@/pages/changePassword/ChangePasswordModal';
import { enablePaginationOrNot } from '@/utils/common';
import { globalSearch } from '@/utils/search/Search';
import {
  EditFilled,
  PlusOutlined,
  SearchOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { Button, Input, Table, Popconfirm, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { useQzTraySetup } from './useUsers';
import { Link } from 'react-router-dom';
import '@/assets/style/modern-group-list.css';
import apiService from '@/apiError/apiServices';
import { useMutation } from '@tanstack/react-query';

const QzTraySetupList = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isShow, setIsShow] = useState();

  const { data, isLoading, refresh, refetch } = useQzTraySetup();
  const [id, setId] = useState(null);

  useEffect(() => {
    const userIsShow = sessionStorage.getItem('isShow');

    setIsShow(userIsShow);
  }, []);

  const useDeleteApi = () => {
    return useMutation({
      mutationFn: (url) => apiService.delete(url),
      onSuccess: (data) => {
        refetch();
      },
      onError: (error) => {},
    });
  };

  const { mutateAsync: deleteApi, isPending: deleteLoading } = useDeleteApi();

  const handleDelete = async (data) => {
    await deleteApi(`/transaction/qz-tray-setup/${data?.referenceId}/`);
    refetch();
  };

  return (
    <>
      <div className="modern-group-page">
        <div className="modern-group-header-row">
          <div className="modern-group-header">
            <h1 className="modern-group-title">QZ Tray Setup</h1>
            <p className="modern-group-subtitle">
              Manage and organize your QZ tray setups
            </p>
          </div>
          <div className="modern-group-actions">
            <Input
              className="header-search"
              name="query"
              value={searchQuery}
              placeholder="Search setup..."
              prefix={<SearchOutlined />}
              onChange={(event) => setSearchQuery(event.target.value)}
              style={{ width: '300px' }}
            />
            <Link to="/transaction/qz-tray-setup/create/" className="tw-w-fit">
              <Button type="primary" className="centered">
                <PlusOutlined />
                Create
              </Button>
            </Link>
          </div>
        </div>

        <div className="table-responsive modern-table-wrapper">
          <Table
            scroll={{ x: true, y: 'calc(100vh - 220px)' }}
            size="large"
            bordered
            loading={isLoading || loading}
            columns={baseColumns?.map((col) => ({
              ...col,
              onCell: () => ({
                style: {
                  padding: '6px 8px',
                },
              }),
            }))}
            expandable={true}
            className="ant-border-space"
            pagination={!isLoading ? enablePaginationOrNot(data?.data) : false}
            dataSource={
              !isLoading
                ? globalSearch(data?.data, searchQuery)?.map((user, index) => ({
                    ...user,
                    action: (
                      <div className="ant-action tw-flex tw-gap-4 tw-justify-center tw-items-center">
                        <Tooltip title="Edit">
                          <Link
                            to={`/transaction/qz-tray-setup/${user.referenceId}/edit/`}
                            className="edit tw-inline-flex tw-cursor-pointer hover:tw-bg-blue-200 tw-rounded-full tw-p-1"
                          >
                            <EditFilled className="tw-text-blue-600" />
                          </Link>
                        </Tooltip>

                        <Popconfirm
                          title="Are you sure to delete this?"
                          onConfirm={() => handleDelete(user)}
                          okText="Yes"
                          cancelText="No"
                          okButtonProps={{
                            style: {
                              padding: '0 8px',
                              height: 24,
                              fontSize: 12,
                            },
                          }}
                          cancelButtonProps={{
                            style: {
                              padding: '0 8px',
                              height: 24,
                              fontSize: 12,
                            },
                          }}
                        >
                          <Tooltip title="Delete">
                            <span className="text-danger tw-inline-flex tw-cursor-pointer hover:tw-bg-red-200 tw-rounded-full tw-p-1">
                              <DeleteOutlined className="tw-text-red-600" />
                            </span>
                          </Tooltip>
                        </Popconfirm>
                      </div>
                    ),
                  }))
                : []
            }
          />
        </div>
      </div>
    </>
  );
};

export default QzTraySetupList;

const baseColumns = [
  {
    title: 'S. N.',
    render: (_, __, index) => index + 1,
    align: 'center',
    width: 50,
  },
  {
    title: 'Company Name',
    dataIndex: 'companyName',
    key: 'companyName',
    align: 'left',
    width: 200,
  },

  {
    title: 'Branch Name',
    dataIndex: 'branchName',
    key: 'branchName',
    align: 'left',
    width: 200,
  },

  {
    title: 'Private Key',
    dataIndex: 'privateKey',
    key: 'privateKey',
    align: 'left',
    width: 100,
    render: (text) =>
      text && text.length > 70 ? text.slice(0, 70) + '...' : text,
  },
  {
    title: 'Certificate Key',
    dataIndex: 'certificateKey',
    key: 'certificateKey',
    width: 100,
    render: (text) =>
      text && text.length > 70 ? text.slice(0, 70) + '...' : text,
  },
  {
    title: 'Action',
    width: 50,
    dataIndex: 'action',
    key: 'action',
    align: 'center',
  },
];
